/**
 * The Accounts journey as a reader wants it: one line per signal type, channel
 * and day (events of one day interleave, so "Webinar registered" twice and a
 * page view between them is one line "×2"), and the source of a touch in
 * words instead of a truncated tracking URL.
 */

export interface JourneyLike {
  type: string;
  channel: string;
  occurredAt: string;
  isProspectAction: boolean;
  payload: Record<string, unknown>;
}

/** Same type, channel, side and day collapse into one line with a count; the first occurrence keeps its place. */
export function collapseJourney<T extends JourneyLike>(journey: readonly T[]): Array<{ event: T; count: number }> {
  const out: Array<{ event: T; count: number }> = [];
  const seen = new Map<string, number>();
  for (const j of journey) {
    const key = `${j.type}|${j.channel}|${j.isProspectAction}|${j.occurredAt.slice(0, 10)}`;
    const at = seen.get(key);
    if (at !== undefined) out[at]!.count += 1;
    else {
      seen.set(key, out.length);
      out.push({ event: j, count: 1 });
    }
  }
  return out;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

const words = (slug: string): string => decodeURIComponent(slug).replace(/[-_+]+/g, ' ').replace(/\s+/g, ' ').trim();

/** "https://sequesto.com/resources/webinars/clarification-questions-as-strategy?utm_…" → "clarification questions as strategy". */
export function pageName(url: string): string | null {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    return last ? words(last) : 'home page';
  } catch {
    return null;
  }
}

/** A campaign that is only a number (an ad platform's id) says nothing to a reader. */
const readableCampaign = (c: string | null): string | null => (c && !/^\d+$/.test(c) ? words(c) : null);

/**
 * What the touch was and where it came from: the page or form, then the
 * campaign (when it has a name) and the source and medium.
 */
export function journeyDetail(payload: Record<string, unknown>): string | null {
  const url = str(payload.pageUrl);
  const page = url ? pageName(url) : null;
  const form = str(payload.formName);
  const source = str(payload.utmSource);
  const medium = str(payload.utmMedium);
  const campaign = readableCampaign(str(payload.utmCampaign));
  const from = [source, medium].filter(Boolean).join(' / ');
  return (
    [
      form ?? (page ? `page: ${page}` : null),
      campaign ? `campaign: ${campaign}` : null,
      from ? `from ${from}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || null
  );
}
